import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  BmarkFixingData,
  BmarkFixingType,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  TimezonePromiseLookupService,
} from "shared/cubiq-services";
import { TranslateService } from "@ngx-translate/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BmarkFixingsManagerService } from "./services/bmark-fixings-manager.service";
import { BmarkFixingTileColor } from "shared/cubiq-services/model/benchmarks/enums/BmarkFixingTileColor.enum";
import { BmarkBlotterViewFilterSelectionService } from "./services/bmark-blotter-view-filter-selection.service";
import { BmarkSummaryViewFilterType } from "./models/bmark-summary-view-filter-type.enum";
import { LoggerService } from "@bfx/logger";
import { of, Subscription } from "rxjs";
import { runDetached } from "../../shared/helper-services/run-detached";
import { BMSummarySavedSettingType } from "app/bfx-user-settings/bfx-bmark-summary-settings-preferences.component";
const logger = LoggerService.for(__dirname);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "bmark-summary-fixings",
  templateUrl: "./html/bmark-summary-fixings.html",
  standalone: false,
})
export class BmarkSummaryFixingsComponent implements OnInit, OnDestroy {
  @Output() activeFixing: EventEmitter<any> = new EventEmitter<any>();

  get displayedFixingsType(): BmarkFixingType {
    return this.data.displayedFixingsType;
  }

  @Input()
  set displayedFixingsType(value: BmarkFixingType) {
    this.data.displayedFixingsType = value;

    this.subscribeFixings();
  }

  /**
   * containerFixingsData is shared between both fixings instances (Active & Recent) and should be set by parent's template.
   * Once set by one fixings list, changes will be ignored by the other
   */
  @Input()
  public containerFixingsData: {
    clickedFixingData: BmarkFixingData;
  } = {
    clickedFixingData: null,
  };

  /**********************************************************************/
  /* DATA */
  public data: {
    displayedFixingsType: BmarkFixingType;
    selectedFixingId: string;
    selectedFixingType: BmarkFixingType;
    selectedFixingData: BmarkFixingData;
    displayedFixingsList: Array<BmarkFixingData>;
  } = {
    displayedFixingsType: null,
    selectedFixingId: null,
    selectedFixingType: null,
    selectedFixingData: null,
    displayedFixingsList: [],
  };

  public label: {
    title: {
      activeFixingsKey: string;
      activeFixings: string;
      recentFixingsKey: string;
      recentFixings: string;
    };
  } = {
    title: {
      activeFixingsKey: "bmark_summary.fixings.title.active",
      activeFixings: "Active Fixings",
      recentFixingsKey: "bmark_summary.fixings.title.recent",
      recentFixings: "Recent Fixings",
    },
  };

  private displayFixingSubscription: Subscription;
  private displayFixingListTypeSubscription: Subscription;
  private displayFixingListSubscription: Subscription;
  private settingSubscription: Subscription;
  public displayTZ: CubiqTS.TimeZoneImpl;

  public FixingState = CubiqTS.FixingState;
  public BmarkFixingType = BmarkFixingType;
  public BmarkFixingTileColor = BmarkFixingTileColor;
  public Array = Array;

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private translate: TranslateService,
    private bmarkManager: BmarkFixingsManagerService,
    private viewFilterService: BmarkBlotterViewFilterSelectionService,
    private profileActionService: ProfilePromiseActionService,
    private timezoneLookupService: TimezonePromiseLookupService,
    public cd: ChangeDetectorRef,
  ) {}

  private subscribeFixings() {
    let observable =
      BmarkFixingType.ACTIVE === this.data.displayedFixingsType
        ? this.bmarkManager.activeFixingsList$
        : BmarkFixingType.RECENT === this.data.displayedFixingsType
          ? this.bmarkManager.recentFixingsList$
          : of([]);

    if (this.displayFixingListSubscription != null) {
      this.displayFixingListSubscription.unsubscribe();
    }

    this.displayFixingListSubscription = observable
      .pipe(runDetached(this))
      .subscribe((fixingList: Array<BmarkFixingData>) => {
        if (Array.isArray(fixingList)) {
          // DEV-FIXING - remove log
          //logger.out.warn(`DEV-FIXING: UI-FIXING -> COMPLETE - UPDATE LIST (size: ${fixingList.length})`);
          fixingList = fixingList.filter(
            (value) => value != null && value instanceof BmarkFixingData,
          );
          this.data.displayedFixingsList = fixingList;
        }
      });
  }

  /**********************************************************************/
  /* INITIALIZATION */
  ngOnInit() {
    this.initializeManager();
    this.initializeTranslations();
    this.initialiseTimezone();
  }

  protected initializeManager(): void {
    this.displayFixingSubscription = this.bmarkManager.displayFixing$
      .pipe(runDetached(this))
      .subscribe((fixingData: BmarkFixingData) => {
        if (fixingData instanceof BmarkFixingData) {
          logger.out.info(`BMARK: UI-FIXING -> [${fixingData.id}] COMPLETE - UPDATE DISPLAY`);
          this.data.selectedFixingId = fixingData.id;
          this.data.selectedFixingType = fixingData.type;
          this.data.selectedFixingData = fixingData;

          // Update this always so that any non-interactive changes in active fixing
          // keeps the active fixing id
          this.containerFixingsData.clickedFixingData = fixingData;
        } else {
          this.data.selectedFixingId = "";
          this.data.selectedFixingType = null;
          this.data.selectedFixingData = null;
          this.containerFixingsData.clickedFixingData = null;
        }
      });

    this.subscribeFixings();
  }

  private initializeTranslations(): void {
    this.label.title.activeFixings = this.translate.instant(this.label.title.activeFixingsKey);
    this.label.title.recentFixings = this.translate.instant(this.label.title.recentFixingsKey);
  }

  private initialiseTimezone() {
    const key = ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences;
    // use BenchmarkSummary Preferences too
    this.profileActionService
      .getProfileConfig(key)

      .then((profileValue) => {
        this.initUserSettings(profileValue);
      });

    this.settingSubscription = this.profileActionService
      .getProfileConfigUpdates(key)
      .subscribe((profileValue) => {
        this.initUserSettings(profileValue);
      });
  }

  private initUserSettings(profileValue: string) {
    if (typeof profileValue == "string") {
      const savedSettings: BMSummarySavedSettingType = JSON.parse(profileValue);
      const tzId = savedSettings ? savedSettings.fixingTimeTz : "";
      this.timezoneLookupService.getTimezone(tzId).then((tz) => (this.displayTZ = tz));
      this.cd.markForCheck();
    }
  }

  ngOnDestroy() {
    if (this.displayFixingSubscription) {
      this.displayFixingSubscription.unsubscribe();
      this.displayFixingSubscription = null;
    }
    if (this.displayFixingListSubscription) {
      this.displayFixingListSubscription.unsubscribe();
      this.displayFixingListSubscription = null;
    }
    if (this.displayFixingListTypeSubscription) {
      this.displayFixingListTypeSubscription.unsubscribe();
      this.displayFixingListTypeSubscription = null;
    }
    if (this.settingSubscription) {
      this.settingSubscription.unsubscribe();
      this.settingSubscription = null;
    }
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public displayFixing(fixingData: BmarkFixingData) {
    if (this.containerFixingsData.clickedFixingData === fixingData) return;

    this.containerFixingsData.clickedFixingData = fixingData;
    this.bmarkManager.display(fixingData);
  }
}
